import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/ContactForm';
import { ScrollProgress, ScrollToTop } from '@/components/ScrollAnimations';
import AnimatedPageHero from '@/components/AnimatedPageHero';
import { Helmet } from 'react-helmet-async';
import { Award, Shield, CheckCircle2 } from 'lucide-react';

// All certifications from felixinfotech.com
const certifications = {
  iso: [
    { name: 'ISO 9001:2015', description: 'Quality Management System', image: 'https://felixinfotech.com/img/iso-1.png' },
    { name: 'ISO 27001:2013', description: 'Information Security Management', image: 'https://felixinfotech.com/img/iso-2.png' },
  ],
  vendor: [
    {
      vendor: 'Huawei',
      color: '#E4002B',
      certs: [
        { name: 'HCIE R&S', count: 1, description: 'Huawei Certified ICT Expert - Routing & Switching' },
        { name: 'HCIP R&S', count: 1, description: 'Huawei Certified ICT Professional - Routing & Switching' },
        { name: 'HCIA R&S', count: 2, description: 'Huawei Certified ICT Associate - Routing & Switching' },
        { name: 'HCIA Storage', count: 2, description: 'Huawei Certified ICT Associate - Storage' },
        { name: 'HCS', count: 4, description: 'Huawei Certified Specialist' },
        { name: 'HCPA-IP', count: 1, description: 'Huawei Certified Pre-sales Associate - IP' },
        { name: 'HCPA-IT', count: 1, description: 'Huawei Certified Pre-sales Associate - IT' },
      ]
    },
    {
      vendor: 'Cisco',
      color: '#1BA0D7',
      certs: [
        { name: 'CCNA R&S', count: 3, description: 'Cisco Certified Network Associate - Routing & Switching' },
        { name: 'CCNP R&S', count: 2, description: 'Cisco Certified Network Professional - Routing & Switching' },
      ]
    },
    {
      vendor: 'Juniper',
      color: '#84B135',
      certs: [
        { name: 'JNCIA-JunOS', count: 1, description: 'Juniper Networks Certified Associate - JunOS' },
      ]
    },
    {
      vendor: 'Palo Alto',
      color: '#FA582D',
      certs: [
        { name: 'PCNSE', count: 1, description: 'Palo Alto Networks Certified Network Security Engineer' },
      ]
    },
    {
      vendor: 'Microsoft',
      color: '#00A4EF',
      certs: [
        { name: 'MCP', count: 1, description: 'Microsoft Certified Professional' },
        { name: 'MCSA', count: 2, description: 'Microsoft Certified Solutions Associate' },
      ]
    },
    {
      vendor: 'Fortinet',
      color: '#EE3124',
      certs: [
        { name: 'NSE 1', count: 3, description: 'Fortinet Network Security Expert Level 1' },
        { name: 'NSE 2', count: 3, description: 'Fortinet Network Security Expert Level 2' },
        { name: 'NSE 3', count: 2, description: 'Fortinet Network Security Expert Level 3' },
      ]
    },
  ]
};

// Technology partners
const partners = [
  { name: 'HP', logo: 'https://felixinfotech.com/img/p-001.jpg' },
  { name: 'Dell', logo: 'https://felixinfotech.com/img/p-002.jpg' },
  { name: 'Cisco', logo: 'https://felixinfotech.com/img/p-003.jpg' },
  { name: 'Microsoft', logo: 'https://felixinfotech.com/img/p-004.jpg' },
  { name: 'VMware', logo: 'https://felixinfotech.com/img/p-005.jpg' },
  { name: 'Fortinet', logo: 'https://felixinfotech.com/img/p-006.jpg' },
  { name: 'Lenovo', logo: 'https://felixinfotech.com/img/p-007.jpg' },
  { name: 'Sophos', logo: 'https://felixinfotech.com/img/p-008.jpg' },
  { name: 'Symantec', logo: 'https://felixinfotech.com/img/p-009.jpg' },
  { name: 'Veeam', logo: 'https://felixinfotech.com/img/p-010.jpg' },
  { name: 'AWS', logo: 'https://felixinfotech.com/img/p-011.jpg' },
  { name: 'Kaspersky', logo: 'https://felixinfotech.com/img/p-012.jpg' },
  { name: 'NetApp', logo: 'https://felixinfotech.com/img/p-013.jpg' },
  { name: 'Aruba', logo: 'https://felixinfotech.com/img/p-014.jpg' },
  { name: 'Juniper', logo: 'https://felixinfotech.com/img/p-015.jpg' },
  { name: 'Samsung', logo: 'https://felixinfotech.com/img/p-016.png' },
];

export const CertificationsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const totalCerts = certifications.vendor.reduce((acc, v) => 
    acc + v.certs.reduce((a, c) => a + c.count, 0), 0
  );

  return (
    <>
      <Helmet>
        <title>Certifications - Felix Infotech | ISO & Vendor Certified IT Company</title>
        <meta name="description" content="Felix Infotech holds ISO 9001:2015, ISO 27001:2013 certifications and is an authorized partner of Cisco, Microsoft, Dell, HP, Fortinet, Palo Alto Networks, and more." />
        <meta name="keywords" content="Felix Infotech certifications, ISO 9001 certified IT company, ISO 27001, Cisco certified partner, Microsoft partner, Dell authorized, IT certifications India" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://felixinfotech.com/certifications" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Certifications - Felix Infotech | ISO & Vendor Certified" />
        <meta property="og:description" content="ISO 9001 & 27001 certified. Authorized partner of 40+ global technology vendors." />
        <meta property="og:url" content="https://felixinfotech.com/certifications" />
        <meta property="og:site_name" content="Felix Infotech" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Felix Infotech Certifications" />
        <meta name="twitter:description" content="ISO certified IT company with 40+ vendor certifications." />
      </Helmet>

      <ScrollProgress />
      <Navbar />

      <main>
        <AnimatedPageHero
          title="Excellence Through Recognition"
          subtitle="Certifications & Partnerships"
          description="Industry certifications and partnerships that demonstrate our commitment to quality, security, and excellence."
          badge="Certifications"
          animationType="cyber"
          gradient="from-[#0066CC] to-[#004499]"
          breadcrumbs={[{ label: 'Certifications' }]}
        />

        {/* Stats */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: '2', label: 'ISO Certifications' },
                { value: '15+', label: 'Technology Partners' },
                { value: '18+', label: 'Years Experience' },
                { value: `${totalCerts}+`, label: 'Team Certifications' },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <p className="text-3xl md:text-4xl font-bold text-[#0066CC]">{stat.value}</p>
                  <p className="text-sm text-gray-600 mt-1">{stat.label}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ISO Certifications */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">ISO Certifications</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Our commitment to quality and security is validated by internationally recognized certifications.
              </p>
            </motion.div>

            <div className="flex flex-wrap justify-center gap-8">
              {certifications.iso.map((cert, index) => (
                <motion.div
                  key={cert.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center max-w-xs"
                >
                  <img src={cert.image} alt={cert.name} className="h-32 w-auto mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{cert.name}</h3>
                  <p className="text-sm text-gray-600">{cert.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Vendor Certifications */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Team Certifications</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Our engineers hold certifications from leading technology vendors worldwide.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {certifications.vendor.map((vendor, index) => (
                <motion.div
                  key={vendor.vendor}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${vendor.color}20` }}
                    >
                      <Award className="w-5 h-5" style={{ color: vendor.color }} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{vendor.vendor}</h3>
                  </div>
                  <div className="space-y-3">
                    {vendor.certs.map((cert) => (
                      <div key={cert.name} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#0066CC] flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-gray-900">
                            {cert.name} <span className="text-[#0066CC]">({cert.count})</span>
                          </p>
                          <p className="text-xs text-gray-500">{cert.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Technology Partners */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Technology Partners</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                We partner with the world's leading technology companies to deliver best-in-class solutions.
              </p>
            </motion.div>

            <div className="grid grid-cols-4 md:grid-cols-8 gap-6">
              {partners.map((partner, index) => (
                <motion.div
                  key={partner.name}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.03 }}
                  className="rounded-xl p-4 flex items-center justify-center hover:shadow-md transition-shadow"
                >
                  <img 
                    src={partner.logo} 
                    alt={partner.name}
                    className="h-10 w-auto object-contain"
                    title={partner.name}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <ContactForm />
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
};

export default CertificationsPage;
